/**
 * Referral Tracking Service
 *
 * Handles creating referral records when sales reps invite providers,
 * and converting referrals when providers sign up.
 */

import { supabase } from '../utils/supabase';
import { logger } from '../utils/logger';

/**
 * Generate a unique referral token
 */
const generateReferralToken = () => {
  const timestamp = Date.now().toString(36);
  const randomPart = Math.random().toString(36).substring(2, 10);
  return `ref_${timestamp}_${randomPart}`;
};

/**
 * Create a referral record when a sales rep invites a provider
 *
 * @param {Object} params
 * @param {string} params.referrerId - UUID of the sales rep
 * @param {string} params.referrerEmail - Email of the sales rep
 * @param {string} params.referrerName - Name of the sales rep
 * @param {string} params.inviteeEmail - Email of the invited provider
 * @param {string} params.inviteeName - Name of the invited provider
 * @param {string} [params.inviteeNpi] - NPI number if available
 * @param {string} [params.inviteeSpecialty] - Specialty if available
 * @param {string} [params.inviteeHospital] - Hospital/practice if available
 * @returns {Promise<Object>} The created referral record
 */
export const createReferral = async ({
  referrerId,
  referrerEmail,
  referrerName,
  inviteeEmail,
  inviteeName,
  inviteeNpi,
  inviteeSpecialty,
  inviteeHospital
}) => {
  try {
    // Generate unique token for tracking
    const token = generateReferralToken();

    const { data, error } = await supabase
      .from('referrals')
      .insert({
        referrer_id: referrerId,
        referrer_email: referrerEmail,
        referrer_name: referrerName,
        invitee_email: inviteeEmail,
        invitee_name: inviteeName,
        invitee_npi: inviteeNpi || null,
        invitee_specialty: inviteeSpecialty || null,
        invitee_hospital: inviteeHospital || null,
        invitation_token: token
      })
      .select()
      .single();

    if (error) {
      logger.error('Error creating referral', { error: error.message });
      throw error;
    }

    logger.debug('Referral created', {
      referralId: data.id,
      referrer: referrerEmail,
      invitee: inviteeEmail,
      token
    });

    return data;
  } catch (error) {
    logger.error('Failed to create referral', { error: error.message });
    throw error;
  }
};

/**
 * Check if an email has a pending (unconverted) referral
 *
 * @param {string} email - The email to check
 * @returns {Promise<Object|null>} The referral record or null
 */
export const checkPendingReferral = async (email) => {
  try {
    const { data, error } = await supabase
      .from('referrals')
      .select('*')
      .ilike('invitee_email', email)
      .eq('converted', false)
      .order('invitation_sent_at', { ascending: false })
      .limit(1)
      .single();

    if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
      logger.error('Error checking pending referral', { error: error.message });
      throw error;
    }

    return data || null;
  } catch (error) {
    if (error.code === 'PGRST116') {
      return null;
    }
    logger.error('Failed to check pending referral', { error: error.message });
    return null;
  }
};

/**
 * Convert a referral when the invited provider signs up
 * Uses the database function for atomic operation
 *
 * @param {string} inviteeEmail - Email of the user who signed up
 * @param {string} convertedUserId - UUID of the new user
 * @returns {Promise<Object>} Result with referral info if found
 */
export const convertReferral = async (inviteeEmail, convertedUserId) => {
  try {
    const { data, error } = await supabase
      .rpc('convert_referral_by_email', {
        p_invitee_email: inviteeEmail,
        p_converted_user_id: convertedUserId
      });

    if (error) {
      logger.error('Error converting referral', { error: error.message });
      throw error;
    }

    const result = data?.[0];

    if (result?.was_converted) {
      logger.debug('Referral converted', {
        referralId: result.referral_id,
        referrerId: result.referrer_id,
        referrerEmail: result.referrer_email
      });
    }

    return result || { was_converted: false };
  } catch (error) {
    logger.error('Failed to convert referral', { error: error.message });
    // Don't throw - referral conversion shouldn't block signup
    return { was_converted: false };
  }
};

/**
 * Get referrals for a specific sales rep
 *
 * @param {string} referrerId - UUID of the sales rep
 * @param {Object} [options]
 * @param {number} [options.limit] - Max number of results
 * @param {boolean} [options.convertedOnly] - Only return converted referrals
 * @returns {Promise<Array>} Array of referral records
 */
export const getReferralsForRep = async (referrerId, options = {}) => {
  try {
    let query = supabase
      .from('referrals')
      .select('*')
      .eq('referrer_id', referrerId)
      .order('invitation_sent_at', { ascending: false });

    if (options.convertedOnly) {
      query = query.eq('converted', true);
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching referrals', { error: error.message });
      throw error;
    }

    return data || [];
  } catch (error) {
    logger.error('Failed to fetch referrals', { error: error.message });
    return [];
  }
};

/**
 * Get referral statistics for a sales rep
 *
 * @param {string} referrerId - UUID of the sales rep
 * @param {string} [month] - Optional month filter (YYYY-MM format)
 * @returns {Promise<Object>} Statistics object
 */
export const getReferralStats = async (referrerId, month = null) => {
  try {
    let query = supabase
      .from('referrals')
      .select('id, converted, reward_eligible, reward_paid, invitation_sent_at')
      .eq('referrer_id', referrerId);

    if (month) {
      // Parse month as local timezone (not UTC)
      const [year, monthNum] = month.split('-').map(Number);
      const startDate = new Date(year, monthNum - 1, 1); // month is 0-indexed
      const endDate = new Date(year, monthNum, 1); // First day of next month

      query = query
        .gte('invitation_sent_at', startDate.toISOString())
        .lt('invitation_sent_at', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching referral stats', { error: error.message });
      throw error;
    }

    const referrals = data || [];

    return {
      totalInvitations: referrals.length,
      totalConversions: referrals.filter(r => r.converted).length,
      pendingRewards: referrals.filter(r => r.reward_eligible && !r.reward_paid).length,
      paidRewards: referrals.filter(r => r.reward_paid).length,
      conversionRate: referrals.length > 0
        ? Math.round((referrals.filter(r => r.converted).length / referrals.length) * 100)
        : 0
    };
  } catch (error) {
    logger.error('Failed to fetch referral stats', { error: error.message });
    return {
      totalInvitations: 0,
      totalConversions: 0,
      pendingRewards: 0,
      paidRewards: 0,
      conversionRate: 0
    };
  }
};

/**
 * Get the monthly leaderboard
 *
 * @param {string} [month] - Month in YYYY-MM format, defaults to current month
 * @param {number} [limit] - Max number of results
 * @returns {Promise<Array>} Leaderboard entries
 */
export const getMonthlyLeaderboard = async (month = null, limit = 10) => {
  try {
    // Use current month if not specified (in local timezone)
    const now = new Date();
    const targetMonth = month || `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

    // Parse month as local timezone (not UTC)
    const [year, monthNum] = targetMonth.split('-').map(Number);
    const startDate = new Date(year, monthNum - 1, 1); // month is 0-indexed
    const endDate = new Date(year, monthNum, 1); // First day of next month

    const { data, error } = await supabase
      .from('referrals')
      .select(`
        referrer_id,
        referrer_name,
        referrer_email
      `)
      .gte('invitation_sent_at', startDate.toISOString())
      .lt('invitation_sent_at', endDate.toISOString());

    if (error) {
      logger.error('Error fetching leaderboard', { error: error.message });
      throw error;
    }

    // Aggregate in JavaScript since we can't use the view directly with RLS
    const leaderboard = {};

    for (const ref of (data || [])) {
      if (!leaderboard[ref.referrer_id]) {
        leaderboard[ref.referrer_id] = {
          referrer_id: ref.referrer_id,
          referrer_name: ref.referrer_name,
          referrer_email: ref.referrer_email,
          total_invitations: 0,
          total_conversions: 0
        };
      }
      leaderboard[ref.referrer_id].total_invitations++;
    }

    // Get conversion counts
    const { data: conversions } = await supabase
      .from('referrals')
      .select('referrer_id')
      .gte('invitation_sent_at', startDate.toISOString())
      .lt('invitation_sent_at', endDate.toISOString())
      .eq('converted', true);

    for (const conv of (conversions || [])) {
      if (leaderboard[conv.referrer_id]) {
        leaderboard[conv.referrer_id].total_conversions++;
      }
    }

    // Sort by conversions, then invitations
    return Object.values(leaderboard)
      .sort((a, b) => {
        if (b.total_conversions !== a.total_conversions) {
          return b.total_conversions - a.total_conversions;
        }
        return b.total_invitations - a.total_invitations;
      })
      .slice(0, limit);

  } catch (error) {
    logger.error('Failed to fetch leaderboard', { error: error.message });
    return [];
  }
};

/**
 * Get all referrals for admin view (super admin only)
 * This requires the super admin RLS policy to work
 *
 * @param {Object} [options]
 * @param {string} [options.month] - Filter by month (YYYY-MM format)
 * @param {number} [options.limit] - Max number of results
 * @returns {Promise<Array>} All referral records
 */
export const getAllReferralsForAdmin = async (options = {}) => {
  try {
    let query = supabase
      .from('referrals')
      .select('*')
      .order('invitation_sent_at', { ascending: false });

    if (options.month) {
      // Parse month as local timezone (not UTC)
      const [year, month] = options.month.split('-').map(Number);
      const startDate = new Date(year, month - 1, 1); // month is 0-indexed
      const endDate = new Date(year, month, 1); // First day of next month

      query = query
        .gte('invitation_sent_at', startDate.toISOString())
        .lt('invitation_sent_at', endDate.toISOString());
    }

    if (options.limit) {
      query = query.limit(options.limit);
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching all referrals for admin', { error: error.message });
      throw error;
    }

    return data || [];
  } catch (error) {
    logger.error('Failed to fetch all referrals for admin', { error: error.message });
    return [];
  }
};

/**
 * Get aggregated leaderboard for admin view
 * Groups by referrer and calculates stats
 *
 * @param {string} [month] - Filter by month (YYYY-MM format)
 * @returns {Promise<Array>} Leaderboard entries with stats
 */
export const getAdminLeaderboard = async (month = null) => {
  try {
    let query = supabase
      .from('referrals')
      .select('*');

    if (month) {
      // Parse month as local timezone (not UTC)
      const [year, monthNum] = month.split('-').map(Number);
      const startDate = new Date(year, monthNum - 1, 1); // month is 0-indexed
      const endDate = new Date(year, monthNum, 1); // First day of next month

      query = query
        .gte('invitation_sent_at', startDate.toISOString())
        .lt('invitation_sent_at', endDate.toISOString());
    }

    const { data, error } = await query;

    if (error) {
      logger.error('Error fetching admin leaderboard', { error: error.message });
      throw error;
    }

    // Aggregate by referrer
    const leaderboard = {};

    for (const ref of (data || [])) {
      if (!leaderboard[ref.referrer_id]) {
        leaderboard[ref.referrer_id] = {
          referrer_id: ref.referrer_id,
          referrer_name: ref.referrer_name || 'Unknown',
          referrer_email: ref.referrer_email,
          total_invitations: 0,
          total_conversions: 0,
          pending_rewards: 0,
          paid_rewards: 0,
          referrals: []
        };
      }

      leaderboard[ref.referrer_id].total_invitations++;
      if (ref.converted) {
        leaderboard[ref.referrer_id].total_conversions++;
      }
      if (ref.reward_eligible && !ref.reward_paid) {
        leaderboard[ref.referrer_id].pending_rewards++;
      }
      if (ref.reward_paid) {
        leaderboard[ref.referrer_id].paid_rewards++;
      }
      leaderboard[ref.referrer_id].referrals.push(ref);
    }

    // Calculate conversion rates and sort
    const result = Object.values(leaderboard).map(entry => ({
      ...entry,
      conversion_rate: entry.total_invitations > 0
        ? Math.round((entry.total_conversions / entry.total_invitations) * 100)
        : 0
    }));

    // Sort by conversions first, then invitations
    return result.sort((a, b) => {
      if (b.total_conversions !== a.total_conversions) {
        return b.total_conversions - a.total_conversions;
      }
      return b.total_invitations - a.total_invitations;
    });

  } catch (error) {
    logger.error('Failed to fetch admin leaderboard', { error: error.message });
    return [];
  }
};

/**
 * Mark a referral as reward paid (admin only)
 *
 * @param {string} referralId - UUID of the referral
 * @param {number} [amount] - Reward amount
 * @param {string} [notes] - Notes about the payment
 * @returns {Promise<Object>} Updated referral
 */
export const markRewardPaid = async (referralId, amount = null, notes = null) => {
  try {
    const { data, error } = await supabase
      .from('referrals')
      .update({
        reward_paid: true,
        reward_paid_at: new Date().toISOString(),
        reward_amount: amount,
        reward_notes: notes
      })
      .eq('id', referralId)
      .select()
      .single();

    if (error) {
      logger.error('Error marking reward paid', { error: error.message });
      throw error;
    }

    logger.debug('Reward marked as paid', { referralId, amount });
    return data;
  } catch (error) {
    logger.error('Failed to mark reward paid', { error: error.message });
    throw error;
  }
};

export default {
  createReferral,
  checkPendingReferral,
  convertReferral,
  getReferralsForRep,
  getReferralStats,
  getMonthlyLeaderboard,
  getAllReferralsForAdmin,
  getAdminLeaderboard,
  markRewardPaid
};
